import React, { useState } from 'react';
import InputElement from '../Components/InputElement';
import InputSelectBox from '../Components/InputSelectBox';
import ConfirmationPopup from '../Components/ConfirmationPopup';
import { useRecoilState, useRecoilValue } from 'recoil';
import { ActiveEvents, pageDetails, toaster } from '../Recoil/ActiveEvents';
import tshirtSizeImage from '../images/tshirtsize.png';

const VolunteerRegisterForm = () => {
  const eventDetails = useRecoilValue(ActiveEvents);
  const selectedEvent = useRecoilValue(pageDetails);
  const selectedEventName = eventDetails.length > selectedEvent.currentIndex ? eventDetails[selectedEvent.currentIndex].EventName : '';
  const selectedEventID = eventDetails.length > selectedEvent.currentIndex ? eventDetails[selectedEvent.currentIndex].EventId : '';

  const [form, setForm] = useState({
    EventId: selectedEventID,
    EventName: selectedEventName,
    FullName: '',
    MobileNo: '',
    Emailid: '',
    Address: '',
    TShirtSize: ''
  });
  const [showTShirtSizePopup, setShowTShirtSizePopup] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [volunteerRegistrationId, setVolunteerRegistrationId] = useState(null);
  const [notify, setNotify] = useRecoilState(toaster);

  const tShirt = [{TShirtSize:'XXS [Chest(32 Inches) - Height(24 Inches)]'},{TShirtSize:'XS [Chest(34 Inches) - Height(25 Inches)]'},{TShirtSize:'S [Chest(36 Inches) - Height(26 Inches)]'},{TShirtSize:'M [Chest(38 Inches) - Height(27 Inches)]'},{TShirtSize:'L [Chest(40 Inches) - Height(28 Inches)]'},{TShirtSize:'XL [Chest(42 Inches) - Height(29 Inches)]'},{TShirtSize:'2XL [Chest(44 Inches) - Height(30 Inches)]'},{TShirtSize:'3XL [Chest(46 Inches) - Height(31 Inches)]'},{TShirtSize:'4XL [Chest(48 Inches) - Height(32 Inches)]'},{TShirtSize:'5XL [Chest(50 Inches) - Height(32 Inches)]'}];

  const handleChange = (e) => {
    const { name, value } = e.target;
    const sanitizedValue = name === 'MobileNo' ? value.replace(/\D/g, '').slice(0, 10) : value;

    // when EventName is changed, also set EventId and EventDescription if available
    if (name === 'EventName') {
      const eventObj = eventDetails.find((obj) => obj.EventId == sanitizedValue);
      setForm({ ...form, EventId: eventObj?.EventId || '', EventName: eventObj?.EventName || '', [name]: sanitizedValue });
    } else {
      setForm({ ...form, [name]: sanitizedValue });
    }
  };

  const validate = () => {
    const newErrors = {};
    const phonePattern = /^[0-9]{10}$/;
    if (!form.FullName || form.FullName.trim().length < 3) newErrors.FullName = 'Name is required';
    if (!form.MobileNo || !phonePattern.test(form.MobileNo)) newErrors.MobileNo = 'Valid 10 digit mobile is required';
    if (!form.Emailid || !/\S+@\S+\.\S+/.test(form.Emailid)) newErrors.Emailid = 'Valid email is required';
    if (!form.Address || form.Address.trim().length < 5) newErrors.Address = 'Address is required';
    if (!form.TShirtSize) newErrors.TShirtSize = 'T-shirt size is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getOrderId = async (amount, bookingNo) => {
    try {
      const payload = { Amount: amount, UniqueId: bookingNo };
      const eventURI = process.env.REACT_APP_API_URL + "/Registration/CreateOrder";
      const response = await fetch(eventURI, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const responseData = await response.json();
      if (response.ok) return responseData;
      return "Error while generating order";
    } catch (err) {
      console.error(err);
      return "Error while generating order";
    }
  }

  const intiatePayment = async (uniqueId, registrationId) => {
    const amount = 300; // INR
    const orderId = await getOrderId(amount * 100, uniqueId);
    const options = {
       key:"rzp_live_VWk8aGK4iiUR6z",
       key_secret:"vRL1aos64gLt6hodDnSdD6OR",
    //    key:"rzp_test_srWRviTG6uwcbU",
    //  key_secret:"4LfZlw61ECp8BTG7aNDqTFky",
      amount: amount * 100,
      currency: "INR",
      name: "Kalingarayan Runners",
      description: "Volunteer Registration",
      order_id: orderId,
      handler: function (response) {
        const paymentId = response.razorpay_payment_id;
        if (registrationId) {
          updateVolunteerPayment(registrationId, paymentId);
        } else {
          setSuccess(true);
        }
      },
      prefill: { name: form.FullName, email: form.Emailid, contact: form.MobileNo },
      theme: { color: "#fff" }
    };
    const pay = new window.Razorpay(options);
    pay.open();
  }

  const submitVolunteer = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    await confirmSubmit();
  };

  const createVolunteerRegistration = async () => {
    const eventURI = process.env.REACT_APP_API_URL + "/Registration/CreateVolunteer";
    const payload = {
      Name: form.FullName,
      EventId: form.EventId,
      MobileNo: form.MobileNo,
      EmailId: form.Emailid,
      Address: form.Address,
      TshirtSize: form.TShirtSize,
      Status: "A",
      PaymentId: null
    };

    const response = await fetch(eventURI, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });

    const result = await response.json();
    const registrationId = result?.CreateVolunteer?.[0]?.NewId || result?.CreateVolunteer?.[0]?.RegistrationId || result?.CreateVolunteer?.[0]?.NewID || null;

    if (!response.ok || !registrationId) {
      throw new Error(result?.CreateVolunteer?.[0]?.ErrorMessage || 'Volunteer registration failed');
    }

    return registrationId;
  };

  const resetForm = () => {
    setForm({
      EventId: selectedEventID,
      EventName: selectedEventName,
      FullName: '',
      MobileNo: '',
      Emailid: '',
      Address: '',
      TShirtSize: ''
    });
    setVolunteerRegistrationId(null);
    setErrors({});
  };

  const updateVolunteerPayment = async (registrationId, paymentId) => {
    try {
      const eventURI = process.env.REACT_APP_API_URL + "/Registration/UpdateVolunteerPayment";
      const response = await fetch(eventURI, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ RegistrationId: registrationId, PaymentId: paymentId })
      });

      const result = await response.json();

      if (response.ok && result?.PaymentUpdate?.[0]?.ErrorMessage === 'Success') {
        resetForm();
        setSuccess(true);
      } else {
        throw new Error(result?.PaymentUpdate?.[0]?.ErrorMessage || 'Payment update failed');
      }
    } catch (err) {
      console.error(err);
      setNotify({ showPopup: true, message: err.message || 'Payment update failed', type: 'error' });
    }
  };

  const confirmSubmit = async () => {
    try {
      const registrationId = await createVolunteerRegistration();
      setVolunteerRegistrationId(registrationId);
      await intiatePayment(form.MobileNo || new Date().getTime(), registrationId);
    } catch (err) {
      console.error(err);
      setNotify({ showPopup: true, message: err.message || 'Volunteer registration failed', type: 'error' });
    }
  };

  return (
    <div className='max-w-[800px] mx-auto px-4 py-8'>
      <h2 className='text-dark-gray text-2xl font-medium'>Volunteer Registration</h2>
      <p className='text-sm text-neutral-600'>Register to volunteer for our events.</p>
      <form onSubmit={submitVolunteer} className='space-y-4 pt-6'>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <div>
              <InputSelectBox value={form.EventId} errors={errors.EventName} Label='Name of the Event' elementName='EventName' handleRegChange={(e)=>handleChange(e)} arrOptions={eventDetails} type='EventName' />
            </div>
          <div>
            <InputElement value={form.FullName} errors={errors.FullName} Label='Name *' elementName='FullName' handleRegChange={(e)=>handleChange(e)} />
          </div>
          <div>
            <InputElement value={form.MobileNo} errors={errors.MobileNo} Label='Number *' elementName='MobileNo' handleRegChange={(e)=>handleChange(e)} maxLength={10} />
          </div>
          <div>
            <InputElement value={form.Emailid} errors={errors.Emailid} Label='Gmail *' elementName='Emailid' handleRegChange={(e)=>handleChange(e)} />
          </div>
          <div>
            <InputElement value={form.Address} errors={errors.Address} Label='Address *' elementName='Address' handleRegChange={(e)=>handleChange(e)} />
          </div>
          <div>
            <InputSelectBox value={form.TShirtSize} errors={errors.TShirtSize} Label='T-shirt size *' elementName='TShirtSize' handleRegChange={(e)=>handleChange(e)} arrOptions={tShirt} type='TShirtSize' />
            <button
              type="button"
              onClick={() => setShowTShirtSizePopup(true)}
              className="block mt-2 text-primary-600 hover:text-primary-700 underline font-medium"
            >
              View T-shirt Size Chart in Inches
            </button>
          </div>
        </div>

        <div className='pt-2'>
          <p className='text-sm font-medium'>Payment: ₹300</p>
        </div>

        <div className='pt-4'>
          <button type='submit' className='bg-red-600 text-white py-2 px-6 rounded-md'>Register & Pay</button>
        </div>

        <div className='mt-6 p-4 border-l-4 border-primary-500 bg-primary-50'>
          <strong>Note:</strong>
          <p className='mt-2'>What is included :</p>
          <ul className='list-disc list-inside'>
            <li>Food</li>
            <li>T-shirt</li>
            <li>Energy drinks</li>
          </ul>
        </div>

      </form>

      {/* Map volunteer form shape to the confirmation popup's expected fields */}
      <ConfirmationPopup popup={success} message="Thank you for volunteering!" clearValue={()=>setSuccess(false)} />
      {showTShirtSizePopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-semibold text-gray-900">T-shirt Size Chart</h3>
              <button
                onClick={() => setShowTShirtSizePopup(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="p-6">
              <img src={tshirtSizeImage} alt="T-shirt Size Chart" className="w-full rounded-md" />
            </div>
            <div className="border-t border-gray-200 px-6 py-4">
              <button
                onClick={() => setShowTShirtSizePopup(false)}
                className="w-full bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default VolunteerRegisterForm;
